/**
 * AOP 2027 Planning — "Sync to hiring plan" on an approved department (David, Oct 21 2026).
 *
 * Under the review row of an APPROVED department: what the last sync did, for everyone who sees
 * the tab, and for Anna and David the button itself, after a confirmation, plus each row's outcome.
 * The sync is the proxy's (`proxy-server/lib/aopSync.js`): it writes the approved drafts into
 * hiring_plan as "Planned in AOP", updates rows still Planned in AOP, cancels removed ones, and
 * refuses a department whose drafts changed since the approval. Its refusal is shown word for word.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { AOP_TIMELINE_QUERY_KEY, type AopIterationStatus, type AopSyncResult, syncAopDepartment } from '../../services/aopPlanningApi';
import { day, who } from './aopFormat';

type Entry = AopIterationStatus['departments'][string];

const OUTCOME_LABEL: Record<AopSyncResult['rows'][number]['outcome'], string> = {
  created: 'Created',
  updated: 'Updated',
  cancelled: 'Cancelled',
  skipped: 'Skipped',
  failed: 'Failed',
};

/** "3 created, 1 updated, 1 failed" — the outcomes that happened, in a fixed order. */
export function syncSummary(sync: AopSyncResult): string {
  const parts = (['created', 'updated', 'cancelled', 'skipped', 'failed'] as const)
    .filter((outcome) => sync[outcome] > 0)
    .map((outcome) => `${sync[outcome]} ${outcome}`);
  return parts.length ? parts.join(', ') : 'nothing to do';
}

/** The sync summary and controls for one approved department — see the module note. */
export function AopSyncRow({
  status,
  department,
  entry,
}: {
  status: AopIterationStatus;
  department: string;
  entry: Entry | undefined;
}) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [showRows, setShowRows] = useState(false);
  const sync = useMutation({
    mutationFn: () => syncAopDepartment(department),
    // Pending until the new status is in, so the summary below is already the new one.
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: AOP_TIMELINE_QUERY_KEY });
      setConfirming(false);
      setShowRows(true);
    },
  });

  if (entry?.state !== 'approved') return null;
  // Anna and David: the rows of a sync are theirs to read (the proxy sends them only to them), and
  // starting one is theirs too.
  const canSeeRows = Boolean(status.canApprove);
  const canSync = canSeeRows;
  // The proxy keeps the result with the approval best-effort; if that write failed, the stored
  // one is an OLDER sync, and the answer this page just got is the truth about what was written.
  const last = sync.data && (!entry.sync || entry.sync.at < sync.data.at) ? sync.data : (entry.sync ?? sync.data ?? null);
  const tone = !last ? 'text-zinc-600' : last.failed ? 'text-red-700' : last.skipped ? 'text-amber-700' : 'text-zinc-600';
  const detailsId = `aop-sync-details-${department.replace(/[^a-zA-Z0-9]+/g, '-')}`;

  return (
    <div className="space-y-2 border-t border-zinc-100 pt-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className={tone}>
          {last ? `Last sync to the hiring plan by ${who(last.by)} on ${day(last.at)}: ${syncSummary(last)}.` : 'Not synced to the hiring plan yet.'}
        </span>
        {last && canSeeRows && last.rows.length > 0 && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            aria-expanded={showRows}
            aria-controls={detailsId}
            onClick={() => setShowRows((v) => !v)}
          >
            {showRows ? 'Hide details' : 'Details'}
          </Button>
        )}
        {canSync && (
          <span className="ml-auto">
            <Button
              type="button"
              size="sm"
              disabled={Boolean(entry.stale) || sync.isPending}
              onClick={() => {
                if (sync.isPending) return;
                sync.reset();
                setConfirming(true);
              }}
            >
              {last ? 'Sync again' : 'Sync to hiring plan'}
            </Button>
          </span>
        )}
      </div>
      {canSync && entry.stale && (
        <p className="text-xs text-zinc-500">Sync: the drafts changed since the approval — reopen the department first.</p>
      )}
      {canSeeRows && showRows && last && (
        <ul role="list" id={detailsId} aria-label="Sync details" className="m-0 list-none space-y-0.5 p-0 text-xs text-zinc-600">
          {last.rows.map((row, i) => (
            <li key={`${row.title}-${i}`}>
              <span className={row.outcome === 'failed' ? 'font-medium text-red-700' : row.outcome === 'skipped' ? 'text-amber-700' : ''}>
                {OUTCOME_LABEL[row.outcome]}
              </span>
              {' · '}
              {row.title}
              {row.detail ? <span className="text-zinc-400"> — {row.detail}</span> : null}
            </li>
          ))}
        </ul>
      )}
      {sync.error && !confirming && (
        <p role="alert" className="text-red-700">
          {(sync.error as Error).message}
        </p>
      )}
      {/* Closed once a signature freezes the AOP: the proxy would refuse the sync anyway. */}
      {canSync && confirming && (
        <Modal open onOpenChange={(open: boolean) => !open && !sync.isPending && setConfirming(false)}>
          <ModalContent className="!h-max !w-[min(100vw-2rem,30rem)] gap-4 rounded p-6">
            <ModalHeader>
              <ModalTitle className="!text-lg !font-semibold">Sync {department} to the hiring plan?</ModalTitle>
            </ModalHeader>
            <p className="text-sm text-zinc-600">
              Its {entry.drafts} approved {entry.drafts === 1 ? 'draft goes' : 'drafts go'} into hiring_plan as “Planned in AOP” —
              new ones created with a Position ID, earlier ones updated, removed ones cancelled. Rows TA has already moved on are
              left alone. TA does not count Planned in AOP rows until they are moved to Backlog.
            </p>
            {sync.error && (
              <p role="alert" className="text-sm text-red-700">
                {(sync.error as Error).message}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" size="sm" variant="outline" disabled={sync.isPending} onClick={() => setConfirming(false)}>
                Cancel
              </Button>
              <Button type="button" size="sm" loading={sync.isPending} onClick={() => !sync.isPending && sync.mutate()}>
                Sync
              </Button>
            </div>
          </ModalContent>
        </Modal>
      )}
    </div>
  );
}
